import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const PollioLivePreview = () => {
  return (
    <section className="py-24 bg-slate-950 text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-6 leading-tight">
              Real-Time Results. <br />
              <span className="text-amber-400">Zero Latency Feedback.</span>
            </h2>
            <p className="text-slate-400 text-lg mb-12 leading-relaxed">
              Watch your audience's sentiment transform into visual data instantly. Pollio's live engine handles thousands of simultaneous votes without breaking a sweat.
            </p>
            <div className="space-y-6">
              {[
                { title: "Live Bar Charts", desc: "Visualize choices as they happen with smooth, animated transitions." },
                { title: "Dynamic Word Clouds", desc: "Capture the collective voice through real-time text analysis." },
                { title: "Instant Moderation", desc: "Filter and spotlight the most relevant questions from your audience." }
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                  <div className="flex-shrink-0 mt-1">
                    <div className="bg-amber-500/20 rounded-lg p-2">
                      <SafeIcon icon={FiIcons.FiCheck} className="w-5 h-5 text-amber-400" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-white mb-1">{item.title}</h4>
                    <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="bg-slate-900 rounded-3xl p-8 border border-white/10 shadow-2xl relative z-10">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="font-bold text-white text-lg">Live Poll: Event Feedback</h3>
                  <p className="text-xs text-amber-400 uppercase tracking-widest font-bold">Active • 1.2k Participants</p>
                </div>
                <div className="w-10 h-10 bg-amber-600 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiIcons.FiBarChart2} className="w-6 h-6 text-white" />
                </div>
              </div>

              <div className="space-y-6">
                {[
                  { label: "Amazing Experience", percent: 75, color: "bg-amber-500" },
                  { label: "Good, but room for growth", percent: 18, color: "bg-slate-700" },
                  { label: "Needs Improvement", percent: 7, color: "bg-slate-800" }
                ].map((option, i) => (
                  <div key={i} className="space-y-2">
                    <div className="flex justify-between text-sm font-medium">
                      <span className="text-slate-300">{option.label}</span>
                      <span className="text-white">{option.percent}%</span>
                    </div>
                    <div className="h-3 w-full bg-slate-800 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${option.percent}%` }}
                        transition={{ duration: 1.5, delay: i * 0.2 }}
                        className={`h-full ${option.color}`}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-white/5 flex items-center justify-between">
                <div className="flex -space-x-2">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="w-6 h-6 rounded-full border-2 border-slate-900 bg-slate-800" />
                  ))}
                  <div className="w-6 h-6 rounded-full border-2 border-slate-900 bg-amber-600 flex items-center justify-center text-[8px] font-bold text-white">+1k</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">Live Updates</span>
                </div>
              </div>
            </div>
            {/* Background Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-amber-600/20 blur-[120px] -z-0"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PollioLivePreview;